import React, { useRef, useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Title32, Body16, Title60, Body20 } from 'components/Typography'
import { bp } from 'styles/breakpoints'
import { FadeInUpAnimationWrapper } from 'styles/animation'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'
import ProductCard from 'components/GiftGuideImmersiveProductCard'
import CarouselItemModal from 'components/CarouselItemModal'
import CarouselControls from 'components/Carousel/CarouselControls'
import theme from 'styles/theme'

import {
  Container,
  ProductList,
  ProductContainer,
  StyledSlider,
  CarouselControlsWrapper,
  HeaderContainer,
} from './GiftGuidesProducts.style'

const GiftGuidesProducts = ({
  id,
  title,
  subtitle,
  products,
  hexValue,
  villageName,
  enableImageFadeIn,
}) => {
  const productModalRef = useRef(null)
  const productListRef = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [size, setSize] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  )
  const slider = useRef(null)
  const sliderOnScreen = useRef(null)

  const updateWidth = () => {
    setSize(window.innerWidth)
  }

  useEffect(() => {
    updateWidth()
    window.addEventListener('resize', updateWidth)

    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const onCarouselControlClick = useCallback(
    (i) => {
      const ceiledPosition = Math.ceil(i)

      setCurrentSlide(ceiledPosition)

      if (slider.current) {
        slider.current.slickGoTo(ceiledPosition)
      }
    },
    [slider]
  )

  const settings = {
    arrows: false,
    draggable: true,
    dots: false,
    infinite: false,
    speed: 500,
    focusOnSelect: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    initialSlide: 1,
    useCSS: true,
    centerPadding: 300,
    onInit: () => {
      setCurrentSlide(1)
    },
    beforeChange: (curr, next) => {
      setCurrentSlide(Math.ceil(next))
    },
    afterChange: (curr) => {
      setCurrentSlide(Math.ceil(curr))
    },
    onEdge: () => {},
    responsive: [
      {
        breakpoint: parseInt(bp[3].replace('px', ''), 10),
        settings: {
          initialSlide: 1,
          swipeToSlide: true,
          slidesToShow: 3.5,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: parseInt(bp[1].replace('px', ''), 10),
        settings: {
          slidesToShow: 1.2,
          slidesToScroll: 1,
          initialSlide: 0,
          infinite: true,
          centerMode: true,
          centerPadding: '0px',
        },
      },
    ],
  }

  return (
    <Container name={id} hexValue={hexValue}>
      <HeaderContainer>
        {hexValue ? (
          <>
            <Title32 as="div" color={theme.colors.white}>
              {title}
            </Title32>
            <Body16 as="div" mt={theme.space[2]} color={theme.colors.white}>
              {subtitle}
            </Body16>
          </>
        ) : (
          <>
            <Title60 as="h2">{title}</Title60>
            <Body20 as="div" color={theme.colors.sagemedium}>
              {subtitle}
            </Body20>
          </>
        )}
      </HeaderContainer>

      <div ref={sliderOnScreen} />
      <div
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          position: 'relative',
        }}>
        <ProductList ref={productListRef}>
          <StyledSlider
            onSwipe={() => {
              GA4('gift_guide_products_carousel_scroll', {
                village_name: villageName,
                component_headline: title,
              })
              GA4('carousel_click', { village_name: villageName })
            }}
            {...settings}
            ref={slider}>
            {size &&
              products.map((product, i) => {
                // DX-2352 initial setting of size to null or undefined causes issues with images in carousel so render it only if size is known
                // Ignore the initial and last position of the array if screen size less than 900.
                // This is need because we added an extra position to the init and end of the array to be able to highlight the first and last card of the carousel
                if (
                  size <= parseInt(bp[1].replace('px', ''), 10) &&
                  (i === 0 || i === products.length - 1)
                )
                  return null

                return (
                  <ProductContainer
                    key={`card_${i.toString()}`}
                    display={product.display}
                    selected={
                      i === Math.ceil(currentSlide) + 1 ||
                      i + products.length - 1 === Math.ceil(currentSlide)
                    }>
                    {i !== 0 && i !== products.length - 1 && (
                      <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
                        <ProductCard
                          {...product}
                          hasBackgroundHex={hexValue}
                          selected={
                            i === currentSlide + 1 ||
                            i + products.length - 1 === currentSlide
                          }
                          brand={product?.brand?.brandName}
                          currency={product?.brand?.village?.currency}
                          onclickProductModal={() => {
                            if (productModalRef.current) {
                              trackGTM(
                                'Gift Guides',
                                'gift product card',
                                product?.brand?.brandName,
                                product.description
                              )
                              productModalRef.current.openModal({
                                ...product,
                                currency: product?.brand?.village?.currency,
                                villageName:
                                  product?.brand?.village?.villageName,
                                brand:
                                  product.productBrandNameOverride ||
                                  product?.brand?.brandName,
                              })
                            }
                          }}
                        />
                      </FadeInUpAnimationWrapper>
                    )}
                  </ProductContainer>
                )
              })}
          </StyledSlider>
        </ProductList>
        <CarouselControlsWrapper>
          <CarouselControls
            onSlideChange={onCarouselControlClick}
            currentSlide={currentSlide}
            totalSlides={products.length - 2}
            hasMultipleItemsPerView={false}
            isBoxed={false}
            isSmall={false}
            villageName={villageName}
            carouselType="GiftGuideProduct"
            introductionTitle={title}
          />
        </CarouselControlsWrapper>
      </div>
      <CarouselItemModal ref={productModalRef} />
    </Container>
  )
}

GiftGuidesProducts.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  hexValue: PropTypes.string,
  enableImageFadeIn: PropTypes.bool,
  villageName: PropTypes.string,
}

GiftGuidesProducts.defaultProps = {
  id: '',
  title: '',
  subtitle: '',
  products: [],
  hexValue: PropTypes.string,
  enableImageFadeIn: false,
  villageName: '',
}

export default GiftGuidesProducts
